import Antd from 'ant-design-vue'
import cookies from 'js-cookie'
import { parseJwtPayload } from '@/utils/parseJwt'
import Vue3linkify from 'vue-3-linkify'
import { vTooltip } from 'floating-vue'
import { datadogRum } from '@datadog/browser-rum'
import {
  ModuleRegistry,
  provideGlobalGridOptions,
  CellApiModule,
  RenderApiModule,
  ColumnAutoSizeModule,
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  CsvExportModule,
  ValidationModule,
  RowStyleModule,
  RowSelectionModule,
  QuickFilterModule,
  CellStyleModule,
  CustomEditorModule,
  ColumnApiModule,
  DateFilterModule,
  PaginationModule,
  TextFilterModule,
  TextEditorModule,
  RowAutoHeightModule,
  DragAndDropModule,
  RowDragModule,
  TooltipModule
} from 'ag-grid-community'
import {
  AdvancedFilterModule,
  CellSelectionModule,
  ClipboardModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  MasterDetailModule,
  MultiFilterModule,
  RichSelectModule,
  RowGroupingModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  SetFilterModule
} from 'ag-grid-enterprise'
import OneSignalVuePlugin, { useOneSignal } from '@onesignal/onesignal-vue3'
import Vue3Sanitize from 'vue-3-sanitize'
import {
  Worklife,
  // FormInput, - removed wrapper
  FormButton,
  FormSelect,
  FormTextarea,
  FormToggle,
  FormWrapper,
  FormCheckbox,
  FormRadio,
  // FormAntdInput, - removed wrapper
  FormAntdTextarea,
  FormAntdCheckbox,
  FormAntdRadio,
  FormAntdToggle,
  FormAntdSelect,
  // FormAntdButton, - removed wrapper
  FeatherIcons,
  GlobalModal
} from '@/scripps-components'
import FormSmartSelect from '@/scripps-components/components/form/FormSmartSelect.vue'
import FormSmartToggle from '@/scripps-components/components/form/FormSmartToggle.vue'
import { default as globalFilters } from './_globalsFilters'
import { default as globalAlerts } from './_globalAlerts'
import { default as globalModals } from './_globalModals.js'
import { DownOutlined } from '@ant-design/icons-vue'
import VideoPickerModal from '@/components/VideoPickerModal.vue'
const { ChevronLeftIcon, ChevronRightIcon } = FeatherIcons

// modal window plugin - stayed old v2 format

// Register ag-Grid modules
const gridModules = [
  CellApiModule,
  RenderApiModule,
  ColumnAutoSizeModule,
  PaginationModule,
  TextFilterModule,
  TextEditorModule,
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  CsvExportModule,
  RowStyleModule,
  RowSelectionModule,
  QuickFilterModule,
  CellStyleModule,
  CustomEditorModule,
  ColumnApiModule,
  DateFilterModule,
  RowAutoHeightModule,
  DragAndDropModule,
  RowDragModule,
  TooltipModule,
  AdvancedFilterModule,
  CellSelectionModule,
  ClipboardModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  MasterDetailModule,
  MultiFilterModule,
  RichSelectModule,
  RowGroupingModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  SetFilterModule
]
if (import.meta.env.DEV) {
  gridModules.push(ValidationModule)
}
ModuleRegistry.registerModules(gridModules)
provideGlobalGridOptions({ theme: 'legacy' })

// Export a plugin
export default {
  install(app) {
    // Directives:
    app.directive('tooltip', vTooltip)

    // Plugins:
    // linkify
    app.use(Vue3linkify)
    // onesignal
    try {
      const oneSignalConfig = {
        appId: import.meta.env.VITE_ONESIGNAL_APP_ID,
        allowLocalhostAsSecureOrigin: import.meta.env.VITE_ENV === 'local',
        serviceWorkerParam: { scope: '/onesignal_sw/' },
        serviceWorkerPath: 'OneSignalSDKWorker.js'
      }

      // Add Safari Web ID for local development
      if (import.meta.env.VITE_ENV === 'local' && import.meta.env.VITE_ONESIGNAL_APP_SAFARI_WEB_ID) {
        oneSignalConfig.safari_web_id = import.meta.env.VITE_ONESIGNAL_APP_SAFARI_WEB_ID
      }

      // Installing the plugin calls init, which rejects (uncaught) in a browser without
      // Web Push. Skip it there; the oneSignal store already handles a missing plugin.
      if (useOneSignal().Notifications.isPushSupported()) {
        app.use(OneSignalVuePlugin, oneSignalConfig)

        // turn off logging
        const OneSignal = app.config.globalProperties.$OneSignal
        OneSignal.Debug.setLogLevel('none')
        console.log('Log level set to none for OneSignal via app use')
      }
    } catch (e) {
      console.warn('OneSignal plugin initialization failed:', e)
    }
    // v-html sanitizer
    const defaultSanitizeOptions = {
      allowedTags: [
        'span',
        'p',
        'b',
        'strong',
        'em',
        'u',
        'i',
        'a',
        'br',
        'div',
        'details',
        'summary',
        'ul',
        'li',
        'h1',
        'h2',
        'h3',
        'hr',
        'nd-video',
        'nd-graphic',
        'nd-transition',
        'nd-image'
      ],
      allowedAttributes: {
        '*': ['class', 'id', 'idref'],
        a: ['href', 'target', 'rel'],
        span: ['data-type', 'data-id', 'data-label', 'data-mention-suggestion-char', 'title', 'contenteditable'],
        'nd-video': ['video-type', 'status', 'media-asset-id', 'in-point', 'out-point', 'notes'],
        'nd-graphic': ['subtype', 'template-id', 'data-fields', 'in-point', 'out-point', 'trigger-mode', 'layer'],
        'nd-transition': ['style', 'duration-ms', 'direction', 'notes'],
        'nd-image': ['status', 'media-asset-id', 'display-mode', 'hold-duration-ms', 'caption', 'credit', 'alt-text', 'notes']
      }
    }
    app.use(Vue3Sanitize, defaultSanitizeOptions)
    // ant-design/vue
    app.use(Antd)

    // Global Components:
    // eslint-disable-next-line vue/multi-word-component-names
    app.component('worklife', Worklife)
    app.component('form-wrapper', FormWrapper)
    // app.component('form-input', FormInput) - removed wrapper
    app.component('form-select', FormSelect)
    app.component('form-textarea', FormTextarea)
    app.component('form-button', FormButton)
    app.component('form-toggle', FormToggle)
    app.component('form-checkbox', FormCheckbox)
    app.component('form-radio', FormRadio)
    // app.component('form-antd-input', FormAntdInput) - removed wrapper
    app.component('form-antd-textarea', FormAntdTextarea)
    app.component('form-antd-checkbox', FormAntdCheckbox)
    app.component('form-antd-radio', FormAntdRadio)
    app.component('form-antd-toggle', FormAntdToggle)
    app.component('form-antd-select', FormAntdSelect)
    // app.component('form-antd-button', FormAntdButton) - removed wrapper
    app.component('chevron-left-icon', ChevronLeftIcon)
    app.component('chevron-right-icon', ChevronRightIcon)
    // eslint-disable-next-line vue/multi-word-component-names
    app.component('modal', GlobalModal)
    app.component('a-down-outlined', DownOutlined)
    app.component('video-picker-modal', VideoPickerModal)

    // Global Filters (as global methods):
    app.config.globalProperties.$filters = globalFilters
    // Global Alerts (as global methods):
    app.config.globalProperties.$alerts = globalAlerts
    // Global Modals (as global methods):
    app.config.globalProperties.$modal = globalModals

    // datadog logging
    // If we're not in local development, initialize datadog
    const isProd = import.meta.env.VITE_ENV === 'production'
    const isLocal = import.meta.env.VITE_ENV === 'local'
    const devEnv = import.meta.env.VITE_DEV_ENV || 'scripps'

    if (!isLocal && devEnv === 'scripps') {
      datadogRum.init({
        applicationId: import.meta.env.VITE_DATADOG_APP_ID,
        clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: 'newsdesk',
        env: import.meta.env.VITE_ENV || 'production',
        version: isProd ? import.meta.env.VITE_VERSION : import.meta.env.VITE_GIT_COMMIT_HASH,
        // allowedTracingUrls: [(url) => url.startsWith('https://newsdesk-api.scrippsnet.')],
        sessionSampleRate: 100,
        sessionReplaySampleRate: isProd ? 20 : 0,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        defaultPrivacyLevel: 'mask-user-input',
        beforeSend: (event, context) => {
          // discard 404 network errors
          if (event.http?.status_code === 404) return false
          if (event.http?.status_code === 401) return false

          // discard any errors that contain these messages
          let ignoredMessages = ['ResizeObserver loop limit exceeded', 'timeout', 'Network Error']
          if (event.type === 'error') {
            // Socket failures are reported with websocket context (pusherService)
            // and an auth failure reads exactly like the noise below (ND-2380).
            if (event.context?.websocket) return true
            if (ignoredMessages.some((message) => event.error.message?.includes(message))) return false
            // suppress OneSignal SDK internal race condition errors
            if (event.error.stack?.includes('OneSignalSDK') || event.error.message?.includes('OneSignal')) return false
          }
        }
      })

      const _cookie = cookies.get('EWS_AUTH_KEY') ?? ''
      if (_cookie && _cookie.length > 20) {
        const { profile } = parseJwtPayload(_cookie) ?? {}
        if (profile?.empid) {
          datadogRum.setUser({
            id: profile.empid,
            name: `${profile.first_name} ${profile.last_name}`,
            email: profile.email_address
          })
        }
      }
    }
  }
}
